import { Formik, Form } from 'formik'
import { map, includes, keys, concat, equals, reject, filter, propEq, fromPairs } from 'ramda'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import * as Yup from 'yup'
import { SwitchToggle } from '@/components/form'
import { colors, breakpoints } from '@/styles'
import { Label } from '@/components/text'
import { Button } from '@/components/button'
import { useAddendumModal } from '../context'
import ModalBase from '../modal-base'

const ContentInner = styled.div`
  width: 100%;
  max-width: 540px;
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 540px;
`

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: right;
`

const OptionsList = styled.div`
  margin-bottom: 10px;
  margin-top: -24px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-top: -16px;
  }
`

const ToggleWrapperList = styled(ToggleWrapper)`
  padding: 16px 0;
  margin: unset;

  :not(:last-child) {
    box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.1);
  }
`

const OptionLabel = styled(Label)`
  color: ${colors.grey48};
  font-weight: 400;
`

const PreviewWrapper = styled.div`
  padding-bottom: 24px;
  margin-bottom: -24px;
  box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.1);
  color: ${colors.grey48};
  text-align: center;
  p {
    margin: 0;
  }
`

const FooterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    gap: 8px;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    gap: 4px;
  }
`

const BackButton = styled(Button)`
  min-width: unset;
`

const RemoveButton = styled(Button)`
  min-width: unset;
`

const AllowedLabel = styled(Label)`
  margin-right: 8px;
  font-weight: 500;

  ${({ enabled }) =>
    !enabled &&
    `
      color: ${colors.grey56};
      font-weight: 700;
      font-size: 12px;
    `}
`

const Options = () => {
  const { t } = useTranslation()
  const {
    close,
    backStep,
    addendum,
    addendumType,
    onSubmit,
    onSubmitComplete,
    onDelete,
    onDeleteComplete,
    ModalFooter
  } = useAddendumModal()

  const submitCompleteAction = () => {
    if (onSubmitComplete === 'close') return close()
    if (onSubmitComplete === 'back') return backStep()
    return
  }

  const deleteCompleteAction = () => {
    if (onDeleteComplete === 'close') return close()
    if (onDeleteComplete === 'back') return backStep()
    return
  }

  const handleDelete = async () => {
    await onDelete(addendum)
    deleteCompleteAction()
  }

  const getRightWidget = () => {
    if (onDelete && addendumType === 'one_time') {
      return (
        <RemoveButton theme="delete" onClick={handleDelete}>
          {t('common.remove', 'Remove')}
        </RemoveButton>
      )
    }
    return null
  }

  const allOptions = keys(map(keys)(addendum.metadata?.options || {}))
  const allSelectedOptions = keys(
    filter(propEq('selected', true))(addendum.metadata?.options || {})
  )

  const initialValues = {
    title: addendum?.title || '',
    allowed: addendum?.metadata?.allowed || false,
    options: allSelectedOptions
  }

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(),
    allowed: Yup.bool().required(),
    options: Yup.array()
  })

  const handleOnSubmit = async (values, { setSubmitting }) => {
    const isAllowed = values.allowed && values.options.length > 0
    const formattedOptions = fromPairs(
      allOptions.map(option => {
        const isSelected = includes(option, values.options)
        return [option, { selected: isSelected }]
      })
    )

    const formattedAddendum = {
      listing_contract_addendum_template_id: 1,
      title: values.title,
      content: null,
      data_type: 'options',
      addendum_type: 'standard',
      metadata: {
        allowed: isAllowed,
        options: formattedOptions
      }
    }
    onSubmit(formattedAddendum)
    setSubmitting(false)
    submitCompleteAction()
  }

  const NotAllowedText = () => (
    <>
      <p style={{ marginBottom: '20px' }}>
        We would like to clarify that as per your tenancy agreement, your suite is designated
        non-smoking. This also applies to the use of cannabis, e-cigarettes (vaping) and similar
        devices. Smoking is prohibited inclusive of balconies (limited common area). This is a
        mandatory term for your tenancy. Please note that a breach of a mandatory term will result
        in a notice to end tenancy.
      </p>
      <p>
        Growing marijuana/cannabis is not permitted in the unit, especially due to humidity, mold
        and odor issues. Please note: growing marijuana plants is also not permitted on limited
        common areas such as patios and balconies.
      </p>
    </>
  )

  const AllowedText = ({ values }) => {
    const selectedOptions = values.options
      .map(option => `${t(`l.contract.edit.addendum_modal_smoking_${option}.label`)}`)
      .join(', ')
    return (
      <p>{`Smoking is allowed only in designated areas including: ${selectedOptions}. Anywhere else in the building is strictly prohibited under Strata Bylaw S2.3`}</p>
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        const handleSelect = (id, options) => {
          includes(id, options)
            ? setFieldValue('options', reject(equals(id), options))
            : setFieldValue('options', concat(options, [id]))
        }

        const handleToggle = e => {
          setFieldValue('allowed', e)
          e ? setFieldValue('options', allOptions) : setFieldValue('options', [])
        }

        return (
          <ModalBase
            title={addendum.title}
            subtitle={'Smoking Allowed'}
            backgroundColor={colors.white}
            minHeight={'unset'}
          >
            <ContentInner>
              <StyledForm>
                {values.allowed && (
                  <OptionsList>
                    {allOptions.map(option => {
                      return (
                        <ToggleWrapperList key={option}>
                          <OptionLabel as="label" htmlFor={option}>
                            {t(`l.contract.edit.addendum_modal_smoking_${option}.label`)}
                          </OptionLabel>
                          <SwitchToggle
                            onClick={() => handleSelect(option, values.options)}
                            id={option}
                            enabled={includes(option, values.options)}
                          />
                        </ToggleWrapperList>
                      )
                    })}
                  </OptionsList>
                )}
                <PreviewWrapper>
                  {values.allowed ? <AllowedText values={values} /> : <NotAllowedText />}
                </PreviewWrapper>
                <ModalFooter>
                  <FooterContent>
                    <ButtonsWrapper>
                      <BackButton theme="secondary" onClick={handleSubmit}>
                        {t('common.back')}
                      </BackButton>
                      {getRightWidget()}
                    </ButtonsWrapper>
                    <ToggleWrapper>
                      <AllowedLabel
                        as="label"
                        htmlFor="smoking_allowed"
                        id="smoking_allowed_label"
                        enabled={values.allowed}
                      >
                        {t('l.addendums.edit.smoking_allowed.label', 'Add Smoking Addendums')}
                      </AllowedLabel>
                      <SwitchToggle
                        onClick={handleToggle}
                        name="allowed"
                        enabled={values.allowed}
                        id="smoking_allowed"
                      />
                    </ToggleWrapper>
                  </FooterContent>
                </ModalFooter>
              </StyledForm>
            </ContentInner>
          </ModalBase>
        )
      }}
    </Formik>
  )
}

export default Options
